import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  // Chema's centered navigation intentionally omits Horizon's drawer and
  // header-menu refs. Only require the row that this component still uses.
  requiredRefs = ['headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  #scrollAnchor = 0;
  #positionMarker = null;
  #touchId = null;
  #touchAnchorY = 0;
  #touchDirection = 0;
  #touchIntentUntil = 0;
  #sectionNavigation = false;

  // Explicit section navigation uses one predictable menu inset in either
  // direction, instead of moving the scroll destination as the menu hides.
  setSectionNavigation(active) {
    this.#sectionNavigation = active;
    this.#resetScrollBaseline();
    if (active && this.getAttribute('sticky') === 'scroll-up') {
      this.#cancelExit();
      this.inert = false;
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
    }
  }

  // Native touch scrolling cancels pointer events, so use passive touch events
  // to distinguish intentional upward scrolling from toolbar/layout corrections.
  #handleTouchStart = (event) => {
    if (event.touches.length !== 1) {
      this.#touchId = null;
      this.#touchDirection = 0;
      this.#touchIntentUntil = performance.now() + 800;
      return;
    }
    const touch = event.touches[0];
    this.#touchId = touch.identifier;
    this.#touchAnchorY = touch.clientY;
    this.#touchDirection = 0;
    this.#touchIntentUntil = Infinity;
  };

  #handleTouchMove = (event) => {
    const touch = [...event.touches].find(item => item.identifier === this.#touchId);
    if (!touch || event.touches.length !== 1) return;
    const delta = touch.clientY - this.#touchAnchorY;
    if (Math.abs(delta) >= 8) {
      this.#touchDirection = delta > 0 ? -1 : 1;
      this.#touchAnchorY = touch.clientY;
    }
  };

  #handleTouchEnd = (event) => {
    if ([...event.touches].some(touch => touch.identifier === this.#touchId)) return;
    this.#touchId = null;
    this.#touchIntentUntil = performance.now() + 800;
  };

  #resetScrollBaseline = () => {
    // Mobile browser chrome can resize the viewport during an existing scroll.
    // Keep visibility, but don't count that geometry change as a new direction.
    this.#lastScrollTop = this.#scrollAnchor = Math.max(0, getScrollTop());
  };

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver || !alwaysSticky) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
    this.#lastScrollTop = this.#scrollAnchor = Math.max(0, getScrollTop());
    this.#updateScrollState();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = Math.max(0, getScrollTop());
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    this.#lastScrollTop = scrollTop;

    if (stickyMode === 'always') {
      if (this.getBoundingClientRect().top >= 0) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      return;
    }
    if (stickyMode !== 'scroll-up' || !this.#positionMarker) return;
    if (this.#sectionNavigation) {
      this.#scrollAnchor = scrollTop;
      return;
    }

    // A flow marker does not move when the header becomes sticky. Measuring
    // the sticky header itself made its own visibility feed back into scrolling.
    const naturalTop = this.#positionMarker.getBoundingClientRect().top;
    const active = this.dataset.stickyState === 'active';
    const initialDeparture = !this.dataset.stickyState || this.dataset.stickyState === 'inactive';
    if (naturalTop >= -1 || (initialDeparture && naturalTop + this.offsetHeight > 0)) {
      this.#cancelExit();
      this.dataset.stickyState = 'inactive';
      this.inert = false;
      if (naturalTop >= -1 && this.hasAttribute('data-header-passed')) {
        this.removeAttribute('data-header-passed');
        this.dispatchEvent(new CustomEvent('header:visibility-settled'));
      }
      this.dataset.scrollDirection = 'none';
      this.#scrollAnchor = scrollTop;
      return;
    }

    // First departure uses the same upward slide as later scroll-down hides.
    // Do not swap the language controls while any of this slide is visible.
    if (!active && this.dataset.stickyState !== 'idle') {
      this.dataset.stickyState = 'active';
      this.#startExit();
      this.#scrollAnchor = scrollTop;
      return;
    }

    const visible = active && !this.hasAttribute('data-sticky-exiting');
    if (visible) {
      this.#scrollAnchor = Math.min(this.#scrollAnchor, scrollTop);
      if (scrollTop - this.#scrollAnchor < 16) return;
      this.dataset.scrollDirection = 'none';
      this.#startExit();
      this.#scrollAnchor = scrollTop;
    } else {
      this.#scrollAnchor = Math.max(this.#scrollAnchor, scrollTop);
      if (performance.now() < this.#touchIntentUntil && this.#touchDirection !== -1) {
        // A downward swipe (including its settling momentum) cannot reveal the
        // menu just because native scroll anchoring briefly adjusts scrollTop.
        this.#scrollAnchor = scrollTop;
        return;
      }
      if (this.#scrollAnchor - scrollTop >= 12) {
        this.#cancelExit();
        this.inert = false;
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
        this.#scrollAnchor = scrollTop;
      } else if (!active) {
        this.dataset.stickyState = 'idle';
        this.dataset.scrollDirection = 'none';
      }
    }
  };

  #startExit = () => {
    this.setAttribute('data-sticky-exiting', '');
    // A timeout also settles reduced-motion/no-transition and background tabs.
    const style = getComputedStyle(this);
    const milliseconds = value => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000);
    const durations = style.transitionDuration.split(',').map(milliseconds);
    const delays = style.transitionDelay.split(',').map(milliseconds);
    const duration = Math.max(0, ...durations.map((time, i) => time + delays[i % delays.length]));
    this.#timeout = window.setTimeout(this.#finishExit, duration + 50);
  };

  #cancelExit = () => {
    if (this.#timeout !== null) clearTimeout(this.#timeout);
    this.#timeout = null;
    this.removeAttribute('data-sticky-exiting');
  };

  #finishExit = () => {
    if (!this.hasAttribute('data-sticky-exiting')) return;
    this.dataset.stickyState = 'idle';
    this.#cancelExit();
    this.inert = true;
    this.setAttribute('data-header-passed', '');
    this.dispatchEvent(new CustomEvent('header:visibility-settled'));
  };

  #handleTransitionEnd = (event) => {
    if (event.target === this && event.propertyName === 'translate') this.#finishExit();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    this.#lastScrollTop = this.#scrollAnchor = Math.max(0, getScrollTop());
    if (stickyMode === 'scroll-up') {
      this.#positionMarker = document.createElement('span');
      this.#positionMarker.setAttribute('aria-hidden', 'true');
      this.#positionMarker.style.cssText = 'display:block;height:0;pointer-events:none';
      (this.closest('.header-section') || this).before(this.#positionMarker);
      this.addEventListener('transitionend', this.#handleTransitionEnd);
      document.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      document.addEventListener('touchmove', this.#handleTouchMove, { passive: true });
      document.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
      document.addEventListener('touchcancel', this.#handleTouchEnd, { passive: true });
      window.addEventListener('resize', this.#resetScrollBaseline, { passive: true });
      window.visualViewport?.addEventListener('resize', this.#resetScrollBaseline, { passive: true });
      this.#updateScrollState();
    }
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#cancelExit();
    this.inert = false;
    this.removeEventListener('transitionend', this.#handleTransitionEnd);
    document.removeEventListener('touchstart', this.#handleTouchStart);
    document.removeEventListener('touchmove', this.#handleTouchMove);
    document.removeEventListener('touchend', this.#handleTouchEnd);
    document.removeEventListener('touchcancel', this.#handleTouchEnd);
    window.removeEventListener('resize', this.#resetScrollBaseline);
    window.visualViewport?.removeEventListener('resize', this.#resetScrollBaseline);
    this.#touchId = null;
    this.#touchIntentUntil = 0;
    this.#sectionNavigation = false;
    this.#positionMarker?.remove();
    this.#positionMarker = null;
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      // Entries contain only changed elements, not the whole group. Always
      // measure the full group so a marker/localization resize cannot erase
      // the header offset used by the cover and other scroll animations.
      let headerGroupHeight = [...headerGroup.children].reduce((height, child) => {
        return height + (child instanceof HTMLElement ? child.offsetHeight : 0);
      }, 0);
      if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
        headerGroupHeight += header.offsetHeight;
      }
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
